import * as rxjs from "rxjs";

export type ChiNode = ChiFn | string | number | boolean | null | undefined | ChiNode[];

export interface ChiContext<T> {
  readonly defaultValue: T;
  readonly Provider: (props: { value: T }, children: ChiNode) => ChiNode;
}

export interface ChiScope {
  context: ChiContext<any>;
  value: unknown;
  parent: ChiScope | null;
}

export interface ChiElement {
  tag: string;
  props: Record<string, any>;
  children: ChiItem[];
  path: string;
  scope: ChiScope | null;
}

export type ChiItem = ChiElement | string;

export interface ChiAnchor {
  path: string;
  scope: ChiScope | null;
}

export type ChiSlot = object;

const disposeSlot = (slot: ChiSlot) => (slot as { dispose?: () => void }).dispose?.();

interface Frame {
  path: string;
  hook: number;
  scope: ChiScope | null;
}

const CONTEXT = Symbol("chi.context");

let active: ChiRuntime | null = null;

export function createContext<T>(defaultValue: T): ChiContext<T> {
  const Provider = ((_props: { value: T }, children: ChiNode) => children) as ChiContext<T>[
    "Provider"
  ];
  const context: ChiContext<T> = { defaultValue, Provider };
  Object.defineProperty(Provider, CONTEXT, { value: context });
  return context;
}

export function currentRuntime(): ChiRuntime {
  if (!active) throw new Error("chi: hooks can only be used while a runtime is rendering");
  return active;
}

export function expandDeferred(node: ChiNode, anchor: ChiAnchor): ChiItem[] {
  return currentRuntime().expand(node, anchor.path, anchor.scope);
}

const keyOf = (node: ChiNode): unknown =>
  typeof node === "object" && node !== null && !Array.isArray(node) ? node.props?.key : undefined;

const labelOf = (chi: ChiFn["chi"]) => typeof chi === "string" ? chi : chi.name || "fn";

export class ChiRuntime {
  readonly invalidate$ = new rxjs.Subject<void>();

  readonly #slots = new Map<string, ChiSlot[]>();
  readonly #touched = new Set<string>();
  #effects: (() => void)[] = [];
  #frame: Frame | null = null;
  #passing = false;
  #disposed = false;

  get scope(): ChiScope | null {
    return this.#frame?.scope ?? null;
  }

  get disposed(): boolean {
    return this.#disposed;
  }

  pass<T>(work: () => T): T {
    if (this.#passing) return work();

    const previous = active;
    active = this;
    this.#passing = true;
    try {
      const result = work();
      this.#settle();
      return result;
    } finally {
      this.#passing = false;
      active = previous;
    }
  }

  expand(node: ChiNode, path = "", scope: ChiScope | null = null): ChiItem[] {
    return this.pass(() => this.#expand(node, path, scope));
  }

  slot<T extends ChiSlot>(create: () => T): T {
    const frame = this.#frame;
    if (!frame) throw new Error("chi: hooks can only be used while a component renders");

    let list = this.#slots.get(frame.path);
    if (!list) this.#slots.set(frame.path, list = []);
    this.#touched.add(frame.path);

    const index = frame.hook++;
    if (index >= list.length) list.push(create());
    return list[index] as T;
  }

  afterPass(effect: () => void) {
    this.#effects.push(effect);
  }

  invalidate() {
    if (!this.#disposed) this.invalidate$.next();
  }

  dispose() {
    if (this.#disposed) return;
    this.#disposed = true;
    for (const list of this.#slots.values()) for (const slot of list) disposeSlot(slot);
    this.#slots.clear();
    this.#touched.clear();
    this.#effects = [];
    this.invalidate$.complete();
  }

  #expand(node: ChiNode, path: string, scope: ChiScope | null): ChiItem[] {
    if (node === null || node === undefined || typeof node === "boolean") return [];
    if (typeof node === "string") return [node];
    if (typeof node === "number") return [String(node)];
    if (Array.isArray(node)) {
      return node.flatMap((child, i) => this.#expand(child, `${path}.${keyOf(child) ?? i}`, scope));
    }

    const { chi, props, children } = node;
    const here = `${path}/${keyOf(node) ?? ""}${labelOf(chi)}`;

    if (typeof chi !== "function") {
      return [{
        tag: chi,
        props,
        children: this.#expand(children, here, scope),
        path: here,
        scope,
      }];
    }

    const context = (chi as unknown as { [CONTEXT]?: ChiContext<any> })[CONTEXT];
    if (context) {
      return this.#expand(children, here, { context, value: props.value, parent: scope });
    }

    const previous = this.#frame;
    this.#frame = { path: here, hook: 0, scope };
    let out: ChiNode;
    try {
      out = chi(props, children);
    } finally {
      this.#frame = previous;
    }
    return this.#expand(out, here, scope);
  }

  #settle() {
    for (const [path, list] of this.#slots) {
      if (this.#touched.has(path)) continue;
      for (const slot of list) disposeSlot(slot);
      this.#slots.delete(path);
    }
    this.#touched.clear();

    const effects = this.#effects;
    this.#effects = [];
    for (const effect of effects) effect();
  }
}
